import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SchedulesAPI } from "../api/schedules";
import type {
  TimeOff,
  CreateTimeOffRequest,
  UpdateTimeOffRequest,
} from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { isDemoMode } from "@/features/utils/demo-mode";
import { mockTimeOffs } from "./use-timeoffs.mock";

// Query keys
export const timeoffsKeys = {
  all: ["calendar_timeoffs"] as const,
  timeoffs: (clientId: string) =>
    [...timeoffsKeys.all, "timeoffs", clientId] as const,
  timeoff: (clientId: string, timeoffId: string) =>
    [...timeoffsKeys.all, "timeoffs", clientId, timeoffId] as const,
};

// Query hooks
export function useTimeOffs() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<TimeOff[]>({
    queryKey: timeoffsKeys.timeoffs(clientId),
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(mockTimeOffs);
      }
      return SchedulesAPI.listTimeOffs(clientId);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

// Mutation hooks
export function useCreateTimeOff() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTimeOffRequest) =>
      SchedulesAPI.createTimeOff(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: timeoffsKeys.timeoffs(clientId),
      });
    },
  });
}

export function useUpdateTimeOff() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      timeoffId,
      data,
    }: {
      timeoffId: string;
      data: UpdateTimeOffRequest;
    }) => SchedulesAPI.updateTimeOff(clientId, timeoffId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: timeoffsKeys.timeoffs(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: timeoffsKeys.timeoff(clientId, variables.timeoffId),
      });
    },
  });
}

export function useDeleteTimeOff() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (timeoffId: string) =>
      SchedulesAPI.deleteTimeOff(clientId, timeoffId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: timeoffsKeys.timeoffs(clientId),
      });
    },
  });
}
